/**
 * Security utilities for XSS prevention and input sanitization
 */

/**
 * Escapes HTML special characters to prevent XSS attacks
 * @param {string} text - Text to escape
 * @returns {string} Escaped text safe for HTML insertion
 */
export const escapeHtml = (text) => {
  if (text === null || text === undefined) {
    return '';
  }

  const str = String(text);
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
};

/**
 * Escapes HTML in all string values of an object
 * @param {object} obj - Object with string values to escape
 * @returns {object} Object with escaped string values
 */
export const escapeHtmlInObject = (obj) => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const escaped = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      escaped[key] = escapeHtml(value);
    } else if (Array.isArray(value)) {
      escaped[key] = value.map((item) =>
        typeof item === 'string' ? escapeHtml(item) : item
      );
    } else if (value && typeof value === 'object') {
      escaped[key] = escapeHtmlInObject(value);
    } else {
      escaped[key] = value;
    }
  }
  return escaped;
};

/**
 * Validates and sanitizes URL to prevent XSS via javascript: protocol
 * @param {string} url - URL to validate
 * @param {string[]} allowedProtocols - Allowed URL protocols
 * @returns {string} Sanitized URL or '#' if invalid
 */
export const sanitizeUrl = (url, allowedProtocols = ['http:', 'https:', 'mailto:']) => {
  if (!url || typeof url !== 'string') {
    return '#';
  }

  const trimmed = url.trim();

  // Check for dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:'];
  const lowerUrl = trimmed.toLowerCase();

  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      console.warn('Blocked dangerous URL protocol:', protocol);
      return '#';
    }
  }

  // If URL has protocol, validate it
  if (trimmed.includes(':')) {
    try {
      const parsed = new URL(trimmed);
      if (!allowedProtocols.includes(parsed.protocol)) {
        console.warn('Blocked disallowed URL protocol:', parsed.protocol);
        return '#';
      }
    } catch (error) {
      // Invalid URL format
      console.warn('Invalid URL format:', trimmed);
      return '#';
    }
  }

  return trimmed;
};

/**
 * Sanitizes user input for safe display
 * Removes potentially dangerous characters and limits length
 * @param {string} input - User input to sanitize
 * @param {number} maxLength - Maximum allowed length
 * @returns {string} Sanitized input
 */
export const sanitizeUserInput = (input, maxLength = 10000) => {
  if (input === null || input === undefined) {
    return '';
  }

  let sanitized = String(input);

  // Remove null bytes
  sanitized = sanitized.replace(/\0/g, '');

  // Trim whitespace
  sanitized = sanitized.trim();

  // Limit length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  return sanitized;
};

/**
 * Creates a safe HTML string from a template literal
 * Automatically escapes all interpolated values
 * @param {string[]} strings - Template literal strings
 * @param {...any} values - Template literal values to escape
 * @returns {string} Safe HTML string
 */
export const html = (strings, ...values) => {
  let result = strings[0];

  for (let i = 0; i < values.length; i++) {
    const value = values[i];

    // If value is already marked as safe HTML, don't escape it
    if (value && typeof value === 'object' && value.__safe_html) {
      result += value.value;
    } else {
      result += escapeHtml(value);
    }

    result += strings[i + 1];
  }

  return result;
};

/**
 * Marks a string as safe HTML (already escaped)
 * Use with caution - only for trusted HTML content
 * @param {string} htmlString - HTML string to mark as safe
 * @returns {object} Object with __safe_html flag
 */
export const unsafeHtml = (htmlString) => ({
  __safe_html: true,
  value: htmlString,
});

/**
 * Validates email format
 * @param {string} email - Email address to validate
 * @returns {boolean} True if email format is valid
 */
export const isValidEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return false;
  }

  // Basic email regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * Validates that a string contains only alphanumeric characters and allowed special chars
 * @param {string} input - Input to validate
 * @param {string} allowedChars - Additional allowed characters
 * @returns {boolean} True if valid
 */
export const isAlphanumeric = (input, allowedChars = '') => {
  if (!input || typeof input !== 'string') {
    return false;
  }

  const pattern = new RegExp(`^[a-zA-Z0-9${allowedChars.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]+$`);
  return pattern.test(input);
};

/**
 * Strips all HTML tags from a string
 * @param {string} html - HTML string
 * @returns {string} Plain text without HTML tags
 */
export const stripHtmlTags = (html) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};

/**
 * Truncates text to a maximum length and adds ellipsis
 * Safely handles HTML entities
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @param {string} ellipsis - Ellipsis string
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 100, ellipsis = '...') => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  if (text.length <= maxLength) {
    return text;
  }

  return text.substring(0, maxLength - ellipsis.length) + ellipsis;
};
